import clsx from "clsx"
import { type ReactNode, useState } from "react"

import { AnimatedList } from "../AnimatedList"
import type {
  RenderDragPreview,
  RenderDropIndicator
} from "../AnimatedList/dnd"
import { Button } from "../Button"
import { Text } from "../Text"
import { GroupContextProvider } from "./context"
import styles from "./Group.module.css"

export interface GroupProps<ITEM> {
  items: ITEM[]
  title?: string
  addItemButtonTitle?: string
  maxItems?: number
  getItemKey: (item: ITEM) => string
  children: (item: ITEM, index: number) => ReactNode
  onAddItem?: () => void

  dnd?: {
    scrollOffsetTop?: number
    /**
     * Called when the DND has finished, with the new, re-ordered items.
     */
    onReorder?: (items: ITEM[]) => void
    /**
     * Renders a preview of the item being dragged.
     */
    renderDragPreview?: RenderDragPreview<ITEM>
    /**
     * Renders a drop indicator for the item being dragged.
     */
    renderDropIndicator?: RenderDropIndicator
  }
}

export function Group<ITEM>(props: GroupProps<ITEM>) {
  const {
    title,
    items,
    getItemKey,
    addItemButtonTitle,
    maxItems = Number.MAX_SAFE_INTEGER,
    children,
    dnd,
    onAddItem
  } = props

  const [isDragging, onDragChange] = useState(false)

  const canAddItem = items.length < maxItems

  return (
    <GroupContextProvider itemsCount={items.length}>
      <article
        className={clsx(
          styles.group,
          styles[`border-${isDragging ? "dragging" : "default"}`]
        )}
      >
        <header className={styles.header}>
          <Text component="span" variant="smallBold">
            {title}{" "}
            <Text variant="normal" color="grey11">
              • {items.length}
            </Text>
          </Text>
        </header>

        <AnimatedList
          ariaLabel="Group"
          items={items}
          getItemKey={getItemKey}
          dnd={{
            ...dnd,
            onDragChange
          }}
        >
          {children}
        </AnimatedList>

        <footer className={styles.footer}>
          {canAddItem && (
            <Button
              size="large"
              color="grey"
              startIcon="add"
              onClick={onAddItem}
            >
              {addItemButtonTitle}
            </Button>
          )}
        </footer>
      </article>
    </GroupContextProvider>
  )
}
